import { children, createMemo, JSX, JSXElement, onMount, ParentProps, Show } from "solid-js"
import './st-button.css'

export type ButtonType = 'default' | 'primary' | 'success' | 'info' | 'danger' | 'link' | 'warning' | 'secondary' | 'ghost';

export class ButtonProps {
    enabled?: boolean = true;
    active?: boolean = false;
    tooltip?: string;
    label?: string;
    type?: ButtonType;
    ref?: HTMLButtonElement | ((el: HTMLButtonElement) => void);
    icon?: string;
    loading?: boolean = false;
    focused? = false;
    // Note: If you manually bind a click event using ref, this click handler will not be triggered.
    onClick?: (() => any) | ((e: MouseEvent) => any);
}

function extractAttributeProperties<T extends HTMLElement>(tagName: string, props: Record<string, any>): Partial<JSX.HTMLAttributes<T>> {
    const extractedProps: Partial<JSX.HTMLAttributes<T>> = {};
    const el = document.createElement(tagName);

    const specialAttributes: Record<string, string> = {
        class: 'className',
        for: 'htmlFor',
        tabindex: 'tabIndex',
        readonly: 'readOnly',
        maxlength: 'maxLength',
        colspan: 'colSpan',
        rowspan: 'rowSpan',
        autoplay: 'autoPlay',
        spellcheck: 'spellCheck',
        enctype: 'encType',
        acceptcharset: 'acceptCharset',
        datetime: 'dateTime',
        usemap: 'useMap',
        frameborder: 'frameBorder',
        contenteditable: 'contentEditable',
    };

    for (const key in props) {
        if (key in el || key in specialAttributes) {
            extractedProps[key as keyof JSX.HTMLAttributes<T>] = props[key];
        }
    }

    return extractedProps;
}

export default function Button(props: ParentProps<ButtonProps> & JSX.HTMLAttributes<HTMLButtonElement>) {
    const attributes = extractAttributeProperties<HTMLButtonElement>('button', props);
    const onClick = (e: MouseEvent) => {
        e.stopPropagation();
        props.onClick?.(e);
    }
    
    const type = createMemo(() => props.type || 'default');
    const enabled = createMemo(() => {
        const enabled = props.enabled
        const loading = props.loading

        if (loading == true) return false
        return enabled === undefined ? true : enabled
    });
    const active = createMemo(() => props.active ? 'active' : '');

    const icon = createMemo(() => {
        const paramsIcon = props.icon;
        const loading = props.loading;

        return loading ? 'fa fa-spinner fa-spin' : paramsIcon;
    })

    onMount(() => {
        if (props.focused) {
            setTimeout(() => (props.ref as HTMLButtonElement).focus(), 500);
        }
        if (props.label && props.ref instanceof HTMLButtonElement) {
            props.ref.setAttribute('aria-label', props.label)
        }
    });

    return <button {...attributes} ref={props.ref} type="button"
        class={`${active()} ${props.class ?? ''} st-btn`}
        disabled={!enabled()}
        data-type={type()}
        onClick={(e) => onClick(e)}
        style={props.style}
        use:tooltip={props.tooltip}
    >
        <Show when={!!icon()}>
            <i class={icon()} style={{ 'margin-right': !!props.children ? '0.5rem' : '' }} ></i>
        </Show>
        {props.children}
    </button>
}

Button.defaultProps = new ButtonProps();